import React, { useEffect, useState } from "react";

import {
  DRAG_START_REQUEST,
  END_OVERLAY_DRAG,
  ON_OVERLAY_DRAG,
} from "../../constants/DragOverlayConstants";
import styles from "./DragOverlay.module.css";

/**
 * @typedef {Object} DragState
 * @prop {string} id
 * @prop {number} x
 * @prop {number} y
 *
 * @typedef {import("@/hooks/useOverlayDrag").DragOverlayEvent} DragOverlayEvent
 */

/**
 * Dispatches a an overlay on drag event.
 * @param {import("../../types/DragOverlayTypes.mjs").DragOverlayEventDetail} dragDetail
 */
export const emitOnDrag = (dragDetail) => {
  document.dispatchEvent(
    new CustomEvent(ON_OVERLAY_DRAG, {
      detail: { ...dragDetail },
    }),
  );
};

/**
 * Emits drag end to listeners with final state.
 * @param {DragState} dragDetail
 */
export const emitEnd = (dragDetail) => {
  document.dispatchEvent(
    new CustomEvent(END_OVERLAY_DRAG, { detail: { ...dragDetail } }),
  );
};

/**
 * Controlled by useOverlayDrag hook. A single one should be placed near the app root,
 * and multiple components can subscribe if they use different ids.
 * @returns {React.ReactElement | null}
 */
const DragOverlay = ({}) => {
  const [dragState, setDragState] = useState(/** @type {DragState?} */ (null));

  const reset = () => {
    dragState && emitEnd(dragState);
    setDragState(null);
  };

  /**
   * @param {DragState} param0
   * @returns {void}
   */
  const init = ({ id, x, y }) => setDragState({ id, x, y });

  /**
   * Handles pointerMove event and emits diff from origin as well as mousemove xy to the hook with the matching drag state id
   * @param {PointerEvent} event
   */
  const handleMove = (event) => {
    const { clientX: x, clientY: y, movementX, movementY } = event;
    if (!dragState) return;

    const diff = {
      x: x - dragState.x,
      y: y - dragState.y,
      movementX,
      movementY,
    };
    emitOnDrag({ id: dragState.id, ...diff });
  };

  useEffect(() => {
    /** @param {DragOverlayEvent} event */
    const cb = ({ detail }) => {
      const { id, x, y } = detail;
      if (id && x !== undefined && y !== undefined) {
        init({ id, x, y });
      }
    };

    document.addEventListener(DRAG_START_REQUEST, cb);
    return () => document.removeEventListener(DRAG_START_REQUEST, cb);
  }, []);

  return dragState ? (
    <div
      data-testid="drag-overlay"
      className={styles.overlay}
      onPointerMoveCapture={handleMove}
      onPointerUpCapture={reset}
      // an interrupted touch never sends pointerup; the browser doesn't
      // cancel mouse pointers, so this never fires on desktop
      onPointerCancelCapture={reset}
    ></div>
  ) : null;
};

export default DragOverlay;
